import { queueSnapshot } from '../vault/writeQueue';

/**
 * Whether what this tab is showing is still what the vault holds.
 *
 * The head learns nothing on its own: a recording made on the phone, an arc
 * updated over MCP, a change from another browser all land through the same
 * manifest push, and this tab goes on rendering what it read when it opened.
 * So it asks — and asking is deliberately a POLL rather than a subscription.
 *
 * The reason is the grant, not the effort. `getVaultManifest` is gated on
 * `requireVaultAccess`, which accepts a signed-in caller OR a live grant, and
 * that gate is how one browser is cut off without cutting off the account.
 * Firestore rules cannot see a grant — they see the id token and App Check, and
 * the grant secret travels in the call rather than in the token — so a listener
 * would have to authorise on the ACCOUNT, and a revoked browser would go on
 * hearing about the vault until every device was signed out. Polling stays
 * inside the model that already exists, and pays for itself twice: a refusal
 * here is how a browser finds out it was cut off while it sat idle, rather than
 * on the next thing the person tries to do.
 *
 * What it must never be is chatty. It runs only while the tab is on screen,
 * fires the moment it comes back — which is the case that actually matters,
 * having done something on the phone and turned back to the keyboard — and
 * tells the other tabs of this browser what it learned so they need not ask.
 */

export type PollResult =
  | { kind: 'version'; version: number }
  /** The grant was refused. Not a network problem — this browser was cut off. */
  | { kind: 'cut-off' }
  | { kind: 'unreachable' };

export interface FreshnessView {
  /** The remote manifest version, when it is ahead of what this tab holds.
   *  Transient in the ordinary case — noticing is what starts adopting. */
  behind: number | null;
  cutOff: boolean;
  /**
   * Paths this tab is still writing that ALSO moved somewhere else. Adoption
   * cannot decide these: the queue's copy wins every read and will overwrite
   * the other version when it flushes, so the only honest thing is to say so.
   */
  conflicts: string[];
}

/** Long enough to be nearly free, short enough that a tab left open on a second
 *  screen is not lying for minutes. The focus probe is what carries the
 *  experience; this is the backstop under it. */
const IDLE_MS = 60_000;

/** A failed probe means the network or the backend, and neither is fixed by
 *  asking harder. Reset on the first success. */
const BACKOFF_MS = [60_000, 120_000, 300_000];

const CHANNEL = 'pondering.vault.freshness';

let view: FreshnessView = { behind: null, cutOff: false, conflicts: [] };
const listeners = new Set<() => void>();

export function freshnessSnapshot(): FreshnessView {
  return view;
}

export function subscribeFreshness(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publish(next: FreshnessView): void {
  // Identity is the subscription contract, so it changes only when the answer
  // does — otherwise every probe re-renders the chrome to say the same thing.
  if (
    next.behind === view.behind &&
    next.cutOff === view.cutOff &&
    next.conflicts.length === view.conflicts.length &&
    next.conflicts.every((p, i) => p === view.conflicts[i])
  ) {
    return;
  }
  view = next;
  for (const listener of listeners) listener();
}

function channel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    return new BroadcastChannel(CHANNEL);
  } catch {
    return null;
  }
}

/**
 * Starts asking, and returns the way to stop.
 *
 * [held] is what this tab has synced and [probe] is one question to the
 * backend, both injected — the poller knows nothing about callables or error
 * shapes, which is what makes its rules testable without a vault.
 */
export function watchFreshness({
  held,
  probe,
  onBehind,
}: {
  held: () => number;
  probe: () => Promise<PollResult>;
  /** Run when the vault has moved on. Saying so is only half the job — this is
   *  what goes and gets it. */
  onBehind?: () => void;
}): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;
  let inFlight = false;

  const bus = channel();
  if (bus) {
    bus.onmessage = (event: MessageEvent<{ version?: number }>) => {
      const version = event.data?.version;
      // A sibling already asked. Believing it costs nothing and saves every
      // other tab of this browser a call.
      if (typeof version === 'number') compare(version);
    };
  }

  const compare = (remote: number) => {
    const ahead = remote > held();
    publish({ behind: ahead ? remote : null, cutOff: false, conflicts: view.conflicts });
    if (ahead) onBehind?.();
  };

  const visible = () =>
    typeof document === 'undefined' || document.visibilityState === 'visible';

  const arm = (ms: number) => {
    if (timer) clearTimeout(timer);
    if (stopped) return;
    timer = setTimeout(() => void ask(), ms);
  };

  const ask = async (): Promise<void> => {
    if (stopped || inFlight) return;
    // A hidden tab asks nothing. It is told by whichever tab is on screen, and
    // it probes the moment it comes back.
    if (!visible()) return;
    // A write in flight is about to move the version by itself, so a probe
    // across it would report the tab behind its own edit.
    if (queueSnapshot().state === 'saving') return arm(IDLE_MS);

    inFlight = true;
    try {
      const result = await probe();
      if (stopped) return;
      if (result.kind === 'version') {
        failures = 0;
        compare(result.version);
        bus?.postMessage({ version: result.version });
        arm(IDLE_MS);
      } else if (result.kind === 'cut-off') {
        // Terminal, and the one answer worth interrupting for: nothing this tab
        // does will work again until the phone lets it back in.
        publish({ behind: null, cutOff: true, conflicts: view.conflicts });
        stop();
      } else {
        arm(BACKOFF_MS[Math.min(failures++, BACKOFF_MS.length - 1)]!);
      }
    } finally {
      inFlight = false;
    }
  };

  const onWake = () => {
    if (!visible()) return;
    void ask();
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
  }

  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    timer = null;
    bus?.close();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    }
  };

  void ask();
  arm(IDLE_MS);
  return stop;
}

/** Called once the tab has caught up, so the chrome stops saying otherwise.
 *  Catching up is a gated read that landed, so the grant is live. */
export function markCurrent(): void {
  publish({ behind: null, cutOff: false, conflicts: [] });
}

/** Cut off without this tab's own probe having asked — a sibling holding the
 *  same grant was refused, or the open itself was. */
export function markCutOff(): void {
  publish({ behind: null, cutOff: true, conflicts: view.conflicts });
}

/** What adoption could not take. Replaces rather than accumulates: the set is
 *  recomputed from scratch on every adoption, so a path that stopped clashing
 *  stops being listed. */
export function markConflicts(paths: string[]): void {
  publish({ behind: null, cutOff: view.cutOff, conflicts: paths });
}
